import { API } from "./api";

// Auth helper functions
export const getAuthHeaders = (token: string) => ({
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
});

// Prevent multiple concurrent redirects to login
let isRedirectingToLogin = false;

export function handleExpiredSession() {
    if (isRedirectingToLogin) return;
    isRedirectingToLogin = true;
    localStorage.removeItem("tv_token");
    window.location.href = "/login?session=expired";
}

/**
 * Extract a human-readable message from a FastAPI error body.
 *
 * - `detail` string -> itself.
 * - pydantic list `[{loc, msg}]` -> one "field: msg" line per error (the "body" prefix
 *   dropped; a model-level error has no field and shows its message alone).
 * - `{detail: {message}}` (e.g. the 409 for a failed run) -> the message.
 * Otherwise the fallback: users never see raw JSON.
 */
export function parseApiError(data: any, fallback: string): string {
    if (!data) return fallback;
    const detail = data.detail;
    if (Array.isArray(detail)) {
        const lines = detail.map((e: any) => {
            const msg = String(e?.msg ?? "").replace(/^Value error, /i, "");
            const loc = Array.isArray(e?.loc) ? e.loc.filter((p: any) => p !== "body") : [];
            return loc.length ? `${loc.join(".")}: ${msg}` : msg;
        }).filter(Boolean);
        return lines.length ? lines.join("\n") : fallback;
    }
    if (typeof detail === "string") return detail;
    if (detail && typeof detail.message === "string") return detail.message;
    return fallback;
}

/** The message a failed response throws with: the parsed detail, never raw JSON or HTML. */
export function errorMessageFromBody(text: string, status: number): string {
    const fallback = `API error: ${status}`;
    if (!text) return fallback;
    try {
        return parseApiError(JSON.parse(text), fallback);
    } catch {
        return /^\s*</.test(text) ? fallback : text;
    }
}

// Generic fetch wrapper with error handling
export async function fetchWithAuth<T>(
    endpoint: string,
    token: string,
    options?: RequestInit
): Promise<T> {
    const url = `${API}${endpoint}`;
    const headers = getAuthHeaders(token);

    const response = await fetch(url, {
        ...options,
        headers: {
            ...options?.headers,
            ...headers,
        },
    });

    if (!response.ok) {
        if (response.status === 401) {
            handleExpiredSession();
            // Throw so callers don't proceed with undefined data
            throw new Error("Session expired");
        }
        const text = await response.text();
        throw new Error(errorMessageFromBody(text, response.status));
    }

    return response.json();
}
